import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    Plus,
    Edit2,
    Trash2,
    Calendar,
    MapPin,
    Clock,
    Users,
    Globe,
    Lock,
    Save,
    X,
    ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { startOfMonth, isBefore } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
    { value: 'training', label: 'Training', color: 'bg-blue-500' },
    { value: 'operations', label: 'Operations', color: 'bg-orange-500' },
    { value: 'meeting', label: 'Meeting', color: 'bg-purple-500' },
    { value: 'event', label: 'Event', color: 'bg-green-500' },
    { value: 'other', label: 'Other', color: 'bg-gray-500' },
];

export default function AdminCalendarPage() {
    const navigate = useNavigate();
    const { user, hasUnitAccess, hasGroupAccess } = useAuth();
    const [events, setEvents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEventDialog, setShowEventDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [filterGroup, setFilterGroup] = useState('all');
    
    // Allow access for both unit-level and group-level roles
    const canManageCalendar = hasUnitAccess() || hasGroupAccess();
    
    // Group-level admins can only manage their own group's calendar
    const isGroupLevelAdmin = hasGroupAccess() && !hasUnitAccess();
    const userGroupId = user?.group_id;
    
    // Filter groups for group-level admins - they can only see their group
    const availableGroups = isGroupLevelAdmin 
        ? groups.filter(g => g.id === userGroupId)
        : groups;
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        activity_type: 'training',
        start_time: '',
        end_time: '',
        location: '',
        group_id: '',
        is_public: false
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [eventsRes, groupsRes] = await Promise.all([
                axios.get(`${API_URL}/activities`),
                axios.get(`${API_URL}/groups`)
            ]);
            setEvents(eventsRes.data);
            setGroups(groupsRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            activity_type: 'training',
            start_time: '',
            end_time: '',
            location: '',
            // Group-level admins must create events for their group only
            group_id: isGroupLevelAdmin ? userGroupId : '',
            is_public: false
        });
        setEditingEvent(null);
    };

    const handleOpenCreate = () => {
        resetForm();
        setShowEventDialog(true);
    };

    const handleOpenEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title: event.title,
            description: event.description || '',
            activity_type: event.activity_type,
            start_time: event.start_time ? event.start_time.slice(0, 16) : '',
            end_time: event.end_time ? event.end_time.slice(0, 16) : '',
            location: event.location || '',
            group_id: event.group_id || '',
            is_public: event.is_public || false
        });
        setShowEventDialog(true);
    };

    const handleSave = async () => {
        if (!formData.title || !formData.start_time) {
            toast.error('Title and Start Time are required');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                start_time: new Date(formData.start_time).toISOString(),
                end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null,
                group_id: formData.group_id || null
            };

            if (editingEvent) {
                await axios.put(`${API_URL}/activities/${editingEvent.id}`, payload);
                toast.success('Event updated');
            } else {
                await axios.post(`${API_URL}/activities`, payload);
                toast.success('Event created');
            }
            
            setShowEventDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save event');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!editingEvent) return;
        
        setSubmitting(true);
        try {
            await axios.delete(`${API_URL}/activities/${editingEvent.id}`);
            toast.success('Event deleted');
            setShowDeleteDialog(false);
            setShowEventDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error('Failed to delete event');
        } finally {
            setSubmitting(false);
        }
    };

    const getGroupName = (groupId) => {
        if (!groupId) return 'All Groups';
        const group = groups.find(g => g.id === groupId);
        return group?.name || 'Unknown';
    };

    const getActivityTypeInfo = (type) => {
        return ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[4];
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Filter and group events
    // Group-level admins only see their group's events plus public events
    // Also filter out events from past months
    const currentMonthStart = startOfMonth(new Date());
    
    const filteredEvents = events.filter(event => {
        // Filter out past month events
        if (event.start_time) {
            const eventDate = new Date(event.start_time);
            const eventMonthStart = startOfMonth(eventDate);
            if (isBefore(eventMonthStart, currentMonthStart)) {
                return false;
            }
        }
        
        // Group-level admins: always filter to their group (and public events)
        if (isGroupLevelAdmin) {
            return event.group_id === userGroupId || event.is_public;
        }
        // Unit-level admins: can filter by any group
        if (filterGroup === 'all') return true;
        if (filterGroup === 'public') return event.is_public;
        return event.group_id === filterGroup;
    });

    // Group events by month
    const groupedEvents = filteredEvents.reduce((acc, event) => {
        if (!event.start_time) return acc;
        const date = new Date(event.start_time);
        const monthKey = `${date.toLocaleDateString('en-AU', { month: 'long' })}, ${date.getFullYear()}`;
        if (!acc[monthKey]) acc[monthKey] = [];
        acc[monthKey].push(event);
        return acc;
    }, {});

    if (!canManageCalendar) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-6 text-center">
                        <Calendar className="w-12 h-12 mx-auto mb-4 text-destructive" />
                        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">
                            Only administrators can manage calendar events.
                        </p>
                        <Button onClick={() => navigate('/dashboard')}>
                            Return to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="admin-calendar-page">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/dashboard')}
                            className="text-white hover:bg-white/20"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-lg font-semibold">Manage Calendar</h1>
                    </div>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleOpenCreate}
                        data-testid="create-event-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        New Event
                    </Button>
                </div>
            </header>

            <div className="p-4 space-y-4">
                {/* Filter - hidden for group-level admins since they can only see their group */}
                {!isGroupLevelAdmin && (
                    <div className="flex gap-3">
                        <Select value={filterGroup} onValueChange={setFilterGroup}>
                            <SelectTrigger className="w-48" data-testid="filter-group">
                                <SelectValue placeholder="Filter by Group" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Events</SelectItem>
                                <SelectItem value="public">Public Only</SelectItem>
                                {groups.map((group) => (
                                    <SelectItem key={group.id} value={group.id}>
                                        {group.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                
                {/* Group-level admin info banner */}
                {isGroupLevelAdmin && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            <strong>Group Calendar:</strong> You are viewing events for your group ({availableGroups[0]?.name || 'Your Group'}). 
                            Public events from other groups are also shown.
                        </p>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-24 bg-muted rounded-lg animate-pulse"></div>
                        ))}
                    </div>
                ) : Object.keys(groupedEvents).length === 0 ? (
                    <Card>
                        <CardContent className="p-8 text-center">
                            <Calendar className="w-12 h-12 mx-auto mb-2 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">No events found</p>
                            <Button className="mt-4" onClick={handleOpenCreate}>
                                <Plus className="w-4 h-4 mr-1" />
                                Create First Event
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    Object.entries(groupedEvents).map(([month, monthEvents]) => (
                        <div key={month}>
                            <h2 className="text-lg font-semibold mb-3 text-muted-foreground">{month}</h2>
                            <Card>
                                <CardContent className="p-0 divide-y">
                                    {monthEvents.map((event) => {
                                        const typeInfo = getActivityTypeInfo(event.activity_type);
                                        return (
                                            <div
                                                key={event.id}
                                                className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                                                onClick={() => handleOpenEdit(event)}
                                                data-testid={`event-row-${event.id}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-3 h-3 rounded-sm mt-1.5 ${typeInfo.color}`}></div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="font-medium">{event.title}</p>
                                                            {event.is_public ? (
                                                                <Badge variant="secondary" className="bg-green-100 text-green-700">
                                                                    <Globe className="w-3 h-3 mr-1" />
                                                                    Public
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="outline">
                                                                    <Lock className="w-3 h-3 mr-1" />
                                                                    {getGroupName(event.group_id)}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-foreground">
                                                            <span className="flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                {formatDateTime(event.start_time)}
                                                            </span>
                                                            {event.location && (
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin className="w-3 h-3" />
                                                                    {event.location}
                                                                </span>
                                                            )}
                                                            {event.responses?.length > 0 && (
                                                                <span className="flex items-center gap-1">
                                                                    <Users className="w-3 h-3" />
                                                                    {event.responses.length} responses
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <Button variant="ghost" size="icon">
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </div>
                    ))
                )}
            </div>

            {/* Event Dialog */}
            <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
                        <DialogDescription>
                            {editingEvent ? 'Update the event details.' : 'Create a new calendar event.'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Title *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Event title"
                                data-testid="event-title"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Event description"
                                rows={3}
                                data-testid="event-description"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Type</Label>
                                <Select
                                    value={formData.activity_type}
                                    onValueChange={(value) => setFormData({ ...formData, activity_type: value })}
                                >
                                    <SelectTrigger data-testid="event-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ACTIVITY_TYPES.map((type) => (
                                            <SelectItem key={type.value} value={type.value}>
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${type.color}`}></div>
                                                    {type.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Location</Label>
                                <Input
                                    value={formData.location}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                    placeholder="Event location"
                                    data-testid="event-location"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Start Time *</Label>
                                <Input
                                    type="datetime-local"
                                    value={formData.start_time}
                                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                                    data-testid="event-start"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>End Time</Label>
                                <Input
                                    type="datetime-local"
                                    value={formData.end_time}
                                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                                    data-testid="event-end"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Assign to Group</Label>
                            {isGroupLevelAdmin ? (
                                // Group-level admins can only create events for their own group
                                <div>
                                    <Input 
                                        value={availableGroups.find(g => g.id === userGroupId)?.name || 'Your Group'}
                                        disabled
                                        className="bg-muted"
                                        data-testid="event-group-fixed"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        As a group administrator, you can only create events for your assigned group.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <Select
                                        value={formData.group_id || 'all'}
                                        onValueChange={(value) => setFormData({ ...formData, group_id: value === 'all' ? '' : value })}
                                    >
                                        <SelectTrigger data-testid="event-group">
                                            <SelectValue placeholder="Select group" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Groups (Unit-wide)</SelectItem>
                                            {groups.map((group) => (
                                                <SelectItem key={group.id} value={group.id}>
                                                    {group.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">
                                        If assigned to a specific group, only members of that group will see this event (unless made public).
                                    </p>
                                </>
                            )}
                        </div>
                        
                        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                            <div>
                                <Label className="text-base">Make Public</Label>
                                <p className="text-sm text-muted-foreground">
                                    Public events are visible to all members and allow anyone to sign up.
                                </p>
                            </div>
                            <Switch
                                checked={formData.is_public}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                                data-testid="event-public"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        {editingEvent && (
                            <Button
                                variant="destructive"
                                onClick={() => setShowDeleteDialog(true)}
                                className="w-full sm:w-auto"
                                data-testid="delete-event-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                            </Button>
                        )}
                        <div className="flex-1"></div>
                        <Button variant="outline" onClick={() => setShowEventDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={submitting} data-testid="save-event-btn">
                            <Save className="w-4 h-4 mr-1" />
                            {editingEvent ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Event?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{editingEvent?.title}"? 
                            This will also remove all responses. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
